'use client'
import { format } from 'date-fns'
import { useCallback, useRef, useState } from 'react'
import { IoNotifications } from 'react-icons/io5'
import useSWRInfinite from 'swr/infinite'
import { useShallow } from 'zustand/react/shallow'

import { SuccessResponse } from '@/lib/type-helpers'
import { cn } from '@/lib/utils'

import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { ScrollArea } from '../ui/scroll-area'
import { fetchNotifications } from './actions'
import { useNotificationStore } from './notification-store'

const PAGE_SIZE = 4

const Notifications = () => {
  const [open, setOpen] = useState(false)
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null)
  const reset = useNotificationStore(useShallow((state) => state.reset))
  const fetchNotificationsWithPage = async (page: number) => {
    try {
      const { data, error } = await fetchNotifications({
        page,
        page_size: PAGE_SIZE,
      })

      if (error) {
        throw new Error(error)
      }
      return data
    } catch (err) {
      throw err
    }
  }

  const { data, error, isLoading, size, setSize, isValidating } = useSWRInfinite<SuccessResponse<'/notifications/'>>(
    (pageIndex, previousPageData) => {
      if (previousPageData && !previousPageData.next) return null

      return `notifications?page=${pageIndex + 1}`
    },
    (key) => {
      const page = parseInt(new URLSearchParams(key.split('?')[1]).get('page') || '1', 10)
      return fetchNotificationsWithPage(page)
    },
    {
      initialSize: 1,
    }
  )

  const observer = useRef<IntersectionObserver | null>(null)

  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observer.current) observer.current.disconnect()

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !isLoading && !isValidating) {
          const lastPage = data?.[data.length - 1]
          if (lastPage?.next) {
            setSize(size + 1)
          }
        }
      })

      if (node) observer.current.observe(node)
    },
    [data, isLoading, isValidating, size, setSize]
  )

  const allNotifications = data ? data.flatMap((page) => page.results) : []

  // const notificationLinkMap = {
  //   [NotificationTypes.CONSULTATION]: "/appointments",
  //   [NotificationTypes.HOLIDAY_REQUEST]: "/holidays",
  //   [NotificationTypes.ORDER]: "/requests",
  //   [NotificationTypes.REPEAT_PRESCRIPTION_REQUEST]: "/requests",
  //   [NotificationTypes.RESCHEDULE_REQUEST]: "/requests",
  // };
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (o) reset()
      }}
    >
      <PopoverTrigger onClick={() => setOpen((prev) => !prev)}>
        <IoNotifications className='h-8 w-8 text-primary-yellow' />
      </PopoverTrigger>
      <PopoverContent align='end' className='w-[540px] bg-white p-0'>
        <div className='flex w-full items-center justify-between border-b p-4'>
          <h3 className='text-xl font-semibold'>Notifications</h3>
        </div>

        <ScrollArea className='h-96 rounded-md border'>
          {/* Initial Loading State */}
          {isLoading && !data && <div className='p-8 text-center'>Loading notifications...</div>}

          {/* Error State */}
          {error && <div className='p-8 text-center text-red-500'>Failed to load notifications. Please try again.</div>}

          {/* Notifications List */}
          {allNotifications.map((notification, index) => (
            <div
              // href={"/"}
              key={notification.id}
              ref={index === allNotifications.length - 1 ? lastElementRef : null}
              className={cn(
                'flex gap-4 border-b p-8 py-4',
                index + 1 === allNotifications.length && 'border-0',
                notification.is_read && 'bg-footer-text/10'
              )}
            >
              {/* Unread/Read Indicator */}
              <div
                className={cn(
                  'mt-2 h-3 w-3 rounded-full',
                  notification.is_read ? 'bg-footer-text' : 'bg-accent-orange'
                )}
              />

              {/* Notification Content */}
              <div className='grid gap-2'>
                <h4 className='text-xl font-bold'>{notification.title}</h4>
                <p>{notification.body}</p>
                <p className='text-footer-text'>{format(notification.created, 'dd-MM-yyyy, hh:mm aa')}</p>
              </div>
            </div>
          ))}

          {/* Loading More Indicator */}
          {isValidating && (
            <div ref={loadMoreTriggerRef} className='p-8 text-center'>
              Loading more notifications...
            </div>
          )}

          {/* No Notifications State */}
          {!isLoading && !isValidating && allNotifications.length === 0 && (
            <div className='p-8 text-center text-footer-text'>No notifications found.</div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  )
}

export default Notifications
